import dayjs from 'dayjs';
import React, { useState } from 'react';

import { Grid, InputAdornment } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { Button, Autocomplete, TextField } from 'shared';
import { invoiceFiltersStyles } from '../../styles/styles';
import { STATUS_OPTIONS, FULLFILLMENT_OPTIONS } from '../../utils/watchlistOptions';

const DateRangeFilter = ({ onFilterChange, initialValues = {} }) => {
    const classes = invoiceFiltersStyles();
    const [values, setValues] = useState(() => {
        const getInitialValues = (value, options, isMulti = true) => {
            if (!value) return isMulti ? [] : null;
            if (!isMulti) {
                return options.find((opt) => opt.value === value) || null;
            }
            const values = Array.isArray(value) ? value : [value];
            return values
                .filter(Boolean)
                .map(
                    (val) => options.find((opt) => opt.value === val) || { value: val, label: val },
                );
        };

        return {
            startDate: initialValues.startDate ? dayjs(initialValues.startDate) : null,
            endDate: initialValues.endDate ? dayjs(initialValues.endDate) : null,
            status: getInitialValues(initialValues.status, STATUS_OPTIONS, true),
            fullfillment: getInitialValues(initialValues.fullfillment, FULLFILLMENT_OPTIONS, true),
            customer: initialValues.customer || '',
            eventDays: initialValues.eventDays || '',
        };
    });

    const setFieldValue = (field, value) => {
        setValues({
            ...values,
            [field]: value,
        });
    };

    const handleApply = () => {
        const getValues = (items) =>
            Array.isArray(items) ? items.map((item) => item.value) : items?.value;

        const filters = {
            ...(values.startDate && { startDate: values.startDate.format('YYYY-MM-DD') }),
            ...(values.endDate && { endDate: values.endDate.format('YYYY-MM-DD') }),
            ...(values.status?.length > 0 && { status: getValues(values.status) }),
            ...(values.fullfillment?.length > 0 && {
                fullfillment: getValues(values.fullfillment),
            }),
            ...(values.customer && { customer: values.customer }),
            ...(values.eventDays && { eventDays: values.eventDays }),
        };
        onFilterChange(filters);
    };

    const handleClear = () => {
        const clearedValues = {
            startDate: null,
            endDate: null,
            status: [],
            fullfillment: [],
            customer: '',
            eventDays: '',
        };
        setValues(clearedValues);
        onFilterChange({});
    };

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={4} md={2} lg={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={values.startDate}
                            onChange={(date) => setFieldValue('startDate', date)}
                            label="Start Date"
                            format="MM-DD-YYYY"
                            sx={classes.datePicker}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={4} md={2} lg={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={values.endDate}
                            onChange={(date) => setFieldValue('endDate', date)}
                            label="End Date"
                            minDate={values.startDate}
                            format="MM-DD-YYYY"
                            sx={classes.datePicker}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <Autocomplete
                        multiple
                        options={STATUS_OPTIONS}
                        placeholder="Select Status"
                        name="status"
                        inlineLabel="Status"
                        value={values.status || []}
                        onChange={(e, selectedOptions) => {
                            setFieldValue('status', selectedOptions || []);
                        }}
                        isSearchField={false}
                        customStyle={classes.dropdown}
                        renderTags={(value, getTagProps) => {
                            return value.length > 0 ? `${value.length} selected` : '';
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <Autocomplete
                        multiple
                        options={FULLFILLMENT_OPTIONS}
                        placeholder="Select Fulfillment"
                        name="fullfillment"
                        inlineLabel="Fulfillment"
                        value={values.fullfillment || []}
                        onChange={(e, selectedOptions) => {
                            setFieldValue('fullfillment', selectedOptions || []);
                        }}
                        isSearchField={false}
                        customStyle={classes.dropdown}
                        renderTags={(value, getTagProps) => {
                            return value.length > 0 ? `${value.length} selected` : '';
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        value={values.customer || ''}
                        onChange={(e) => setFieldValue('customer', e.target.value)}
                        placeholder="Customer"
                        sx={classes.inputField}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        value={values.eventDays || ''}
                        placeholder="Days"
                        onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*$/.test(val)) {
                                setFieldValue('eventDays', e.target.value);
                            }
                        }}
                        sx={classes.inputField}
                    />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Button
                            text="Clear"
                            onClick={handleClear}
                            customStyles={classes.cancelButton}
                        />
                        <Button
                            text="Apply"
                            customStyles={{
                                ...classes.applyButton,
                                minWidth: '100px',
                            }}
                            onClick={handleApply}
                        />
                    </div>
                </Grid>
            </Grid>
        </>
    );
};

export default DateRangeFilter;
